import React from 'react';
import { Button } from './ui/button';
import { Play, Star } from 'lucide-react';
import { Card, CardContent } from './ui/card';
// Background image from public folder
const splashImage = '/images/global/freepik__enhance__83475.jpg';
const glitchVideo = '/animation/CFWebGlitch1024_1.mp4';

export function Hero() {
  // Mobile detection for performance optimizations
  const [isMobile, setIsMobile] = React.useState(false);
  const [videoLoaded, setVideoLoaded] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with 70% opacity */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-70"
        style={{
          backgroundImage: `url(${splashImage})`,
        }}
      />
      
      {/* Cyberpunk Glitch Video Overlay - Performance optimized */}
      {!isMobile && (
        <video
          className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-60' : 'opacity-0'
          }`}
          style={{
            mixBlendMode: 'color-dodge',
          }}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedData={() => setVideoLoaded(true)}
          onError={() => console.log('Video failed to load')}
        >
          <source src={glitchVideo} type="video/mp4" />
        </video>
      )}
      
      {/* Mobile: Static glitch effect instead of video for performance */}
      {isMobile && (
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: `
              radial-gradient(circle at 20% 80%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(251, 146, 60, 0.05) 0%, transparent 50%)
            `,
            mixBlendMode: 'color-dodge',
          }}
        />
      )}
      
      {/* Overlay - Slightly darker on mobile for better text readability */}
      <div className={`absolute inset-0 ${
        isMobile 
          ? 'bg-gradient-to-r from-slate-900/60 via-slate-900/60 to-slate-900/60' 
          : 'bg-gradient-to-r from-slate-900/40 via-slate-900/40 to-slate-900/40'
      }`}></div>
      
      {/* Animated Background Elements - Disabled on mobile for performance */}
      {!isMobile && (
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-purple-600/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-purple-600/10 to-pink-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-orange-500/5 to-cyan-500/5 rounded-full blur-3xl"></div>
        </div>
      )}

      {/* Mobile-optimized minimal background effects */}
      {isMobile && (
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-to-r from-cyan-500/5 to-purple-600/5 rounded-full mobile-no-transform"></div>
        </div>
      )}

      {/* Desktop Layout - Original positioning */}
      <div className="container mx-auto px-6 relative z-10 mt-44 hidden lg:block">
        <div className="text-center">
          <div className="mb-8">
            {/* Cyberpunk-style text with decorative elements - 2x bigger with animations */}
            <div className="inline-block mb-6 relative">
              <div className="flex items-center justify-center space-x-3 text-cyan-400">
                <span className="text-purple-400 opacity-70 text-2xl cyberpunk-bracket-pulse">[</span>
                <div className="relative overflow-hidden">
                  <span className="text-cyan-400 tracking-wider uppercase text-2xl font-cyberpunk bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent relative z-10 cyberpunk-glow">
                    Next-Gen Gaming
                  </span>
                  {/* Laser scan effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/15 to-transparent transform translate-x-full cyberpunk-sheen w-4 shadow-lg">
                  </div>
                  {/* Additional laser glow */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-300/10 to-transparent transform translate-x-full cyberpunk-sheen w-8 blur-sm">
                  </div>
                </div>
                <span className="text-purple-400 opacity-70 text-2xl cyberpunk-bracket-pulse">]</span>
              </div>
              {/* Subtle underline effect with pulsing */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent cyberpunk-underline">
              </div>
            </div>
            
            {/* Main heading with lighter, more transparent card background */}
            <Card className="bg-gradient-to-r from-slate-900/30 via-slate-800/20 to-slate-900/30 backdrop-blur-md border-0 shadow-2xl max-w-6xl mx-auto">
              <CardContent className="p-8 md:p-12">
                <h1 className="text-lg md:text-xl lg:text-2xl bg-gradient-to-r from-cyan-400 via-purple-500 to-orange-400 bg-clip-text text-transparent leading-tight">
                  Step into the future of gaming with immersive worlds, cutting-edge graphics, and epic adventures that push the boundaries of what's possible.
                </h1>
              </CardContent>
            </Card>
          </div>

          {/* Single centered Play Now button */}
          <div className="flex justify-center items-center mb-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-12 py-6 text-xl rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl"
              onClick={() => {
                const goosebumpsSection = document.getElementById('game-2');
                if (goosebumpsSection) {
                  goosebumpsSection.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}
            >
              Play Now
              <Play className="ml-3 h-6 w-6 fill-current" />
            </Button>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div className="px-6 py-4 bg-slate-800/50 backdrop-blur-sm rounded-lg border border-cyan-500/20">
              <div className="text-2xl font-bold text-cyan-400">10M+</div>
              <div className="text-sm text-gray-400">Worlwide Players</div>
            </div>
            <div className="px-6 py-4 bg-slate-800/50 backdrop-blur-sm rounded-lg border border-purple-500/20">
              <div className="text-2xl font-bold text-purple-400">100M+</div>
              <div className="text-sm text-gray-400">Social Media Interactions</div>
            </div>
            <div className="px-6 py-4 bg-slate-800/50 backdrop-blur-sm rounded-lg border border-orange-500/20">
              <div className="flex items-center justify-center gap-1 mb-1">
                {[...Array(4)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-orange-400 fill-current" />
                ))}
                <Star className="w-5 h-5 text-orange-400/60 fill-current" />
                <span className="text-lg font-bold text-orange-400 ml-1">4+</span>
              </div>
              <div className="text-sm text-gray-400">User Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Content positioned using CSS control variables with optimized animations */}
      <div className="container mx-auto px-6 relative z-10 lg:hidden">
        {/* Mobile Content Container - Uses CSS variables for positioning control */}
        <div className="mobile-hero-container flex flex-col justify-end">
          <div className="mobile-hero-content">
            <div className="text-center mobile-hero-spacing">
              {/* Mobile Next-Gen Gaming - Optimized animations */}
              <div className="inline-block relative">
                <div className="flex items-center justify-center space-x-2 text-cyan-400">
                  <span className="text-purple-400 opacity-70 text-lg cyberpunk-bracket-pulse">[</span>
                  <div className="relative overflow-hidden">
                    <span className="text-cyan-400 tracking-wider uppercase text-lg font-cyberpunk bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent relative z-10 cyberpunk-glow">
                      Next-Gen Gaming
                    </span>
                    {/* Simplified laser effects for mobile performance */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/8 to-transparent transform translate-x-full cyberpunk-sheen w-3">
                    </div>
                  </div>
                  <span className="text-purple-400 opacity-70 text-lg cyberpunk-bracket-pulse">]</span>
                </div>
                {/* Mobile underline */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent cyberpunk-underline">
                </div>
              </div>
              
              {/* Mobile Main heading - More compact with reduced blur */}
              <Card className={`bg-gradient-to-r from-slate-900/40 via-slate-800/30 to-slate-900/40 border-0 shadow-xl ${
                isMobile ? 'mobile-reduce-blur' : 'backdrop-blur-sm'
              }`}>
                <CardContent className="p-6">
                  <h1 className="text-base md:text-lg bg-gradient-to-r from-cyan-400 via-purple-500 to-orange-400 bg-clip-text text-transparent leading-tight">
                    Step into the future of gaming with immersive worlds, cutting-edge graphics, and epic adventures.
                  </h1>
                </CardContent>
              </Card>

              {/* Mobile Play Now button - More compact */}
              <div className="flex justify-center items-center">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-8 py-4 text-lg rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl touch-manipulation"
                  onClick={() => {
                    const goosebumpsSection = document.getElementById('game-2');
                    if (goosebumpsSection) {
                      goosebumpsSection.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                >
                  Play Now
                  <Play className="ml-2 h-5 w-5 fill-current" />
                </Button>
              </div>

              {/* Mobile Stats - Simplified background effects */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 text-center">
                <div className={`px-4 py-3 bg-slate-800/50 rounded-lg border border-cyan-500/20 ${
                  isMobile ? 'mobile-reduce-blur' : 'backdrop-blur-sm'
                }`}>
                  <div className="text-xl font-bold text-cyan-400">10M+</div>
                  <div className="text-xs text-gray-400">Worldwide Players</div>
                </div>
                <div className={`px-4 py-3 bg-slate-800/50 rounded-lg border border-purple-500/20 ${
                  isMobile ? 'mobile-reduce-blur' : 'backdrop-blur-sm'
                }`}>
                  <div className="text-xl font-bold text-purple-400">100M+</div>
                  <div className="text-xs text-gray-400">Social Interactions</div>
                </div>
                <div className={`px-4 py-3 bg-slate-800/50 rounded-lg border border-orange-500/20 ${
                  isMobile ? 'mobile-reduce-blur' : 'backdrop-blur-sm'
                }`}>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-orange-400 fill-current" />
                    ))}
                    <Star className="w-4 h-4 text-orange-400/60 fill-current" />
                    <span className="text-lg font-bold text-orange-400 ml-1">4+</span>
                  </div>
                  <div className="text-xs text-gray-400">User Rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Simplified on mobile */}
      <div className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 ${
        isMobile ? 'mobile-simple-scroll animate-bounce' : 'animate-bounce'
      }`}>
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className={`w-1 h-3 bg-white/60 rounded-full mt-2 ${
            isMobile ? 'mobile-simple-scroll animate-pulse' : 'animate-pulse'
          }`}></div>
        </div>
      </div>

    </section>
  );
}